import { Link } from "react-router-dom";
import type { GossipEvent } from "@/types/content";
import { formatDate, statusStyleMap } from "@/utils/content";

interface EventSidebarProps {
  event: GossipEvent;
  related: GossipEvent[];
}

export default function EventSidebar({ event, related }: EventSidebarProps) {
  const typeCount = event.evidences.reduce<Record<string, number>>((acc, item) => {
    acc[item.type] = (acc[item.type] ?? 0) + 1;
    return acc;
  }, {});
  const typeEntries = Object.entries(typeCount);

  return (
    <aside className="space-y-5">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950">事件概况</h2>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">事件类型</dt>
            <dd className="text-foreground-950">{event.category}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">整理状态</dt>
            <dd>
              <span className={`px-2.5 py-1 rounded-md text-xs border ${statusStyleMap[event.status] ?? ""}`}>
                {event.status}
              </span>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">最后更新</dt>
            <dd className="text-foreground-950">{formatDate(event.updatedAt)}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">证据条目</dt>
            <dd className="text-foreground-950">{event.evidenceCount} 条</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-foreground-500">当前热度</dt>
            <dd className="text-accent-300 font-medium">{event.heat} / 100</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950">本页证据构成</h2>
        <ul className="mt-4 space-y-3">
          {typeEntries.map(([type, count]) => (
            <li key={type} className="flex items-center justify-between text-sm">
              <span className="text-foreground-600">{type}</span>
              <span className="text-foreground-950">{count} 条</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs leading-6 text-foreground-500">
          类型分布只反映本页已整理的部分，完整素材仍在持续补充。
        </p>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950">同类事件</h2>
        <ul className="mt-4 space-y-4">
          {related.map((item) => (
            <li key={item.id}>
              <Link to={`/event/${item.id}`} className="group block cursor-pointer">
                <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {item.category} · {item.evidenceCount} 条证据
                </p>
              </Link>
            </li>
          ))}
          {related.length === 0 && <li className="text-sm text-foreground-500">该类型暂无其他收录事件。</li>}
        </ul>
      </div>

      <div className="rounded-lg border border-primary-300/40 bg-primary-100/50 p-5 md:p-6">
        <h2 className="font-heading text-base font-bold text-foreground-950">补充或纠正这条素材</h2>
        <p className="mt-2 text-sm leading-6 text-foreground-600">
          如果你有原始链接、完整录音或更早的截图，欢迎提交给我们做来源核对。
        </p>
        <Link
          to="/about#submit"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-send-plane-line" />
          提交线索
        </Link>
      </div>
    </aside>
  );
}